import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@/icons';
import { CustomToggle } from './CustomToggle';

interface FinancialAffidavitModalProps {
    isOpen: boolean;
    onClose: () => void;
    onDownload: (date: string | null, isBlank: boolean, signatureBlockType: 'notarySignatureBlock' | 'penaltyOfPerjury') => void;
    initialSignatureBlockType?: 'notarySignatureBlock' | 'penaltyOfPerjury' | null;
}

export const FinancialAffidavitModal: React.FC<FinancialAffidavitModalProps> = ({
    isOpen,
    onClose,
    onDownload,
    initialSignatureBlockType = null,
}) => {
    const theme = useTheme();
    const [date, setDate] = useState<string>('');
    const [isBlank, setIsBlank] = useState<boolean>(true);
    const [signatureBlockType, setSignatureBlockType] = useState<'notarySignatureBlock' | 'penaltyOfPerjury'>('penaltyOfPerjury');
    const mouseDownOutsideRef = useRef<boolean>(false);

    // Reset form when modal closes
    useEffect(() => {
        if (isOpen) {
            setSignatureBlockType(initialSignatureBlockType === 'notarySignatureBlock' ? 'notarySignatureBlock' : 'penaltyOfPerjury');
        } else {
            setDate('');
            setIsBlank(true);
        }
    }, [isOpen, initialSignatureBlockType]);

    const handleMouseDown = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            mouseDownOutsideRef.current = true;
        } else {
            mouseDownOutsideRef.current = false;
        }
    };

    const handleMouseUp = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget && mouseDownOutsideRef.current) {
            onClose();
        }
        mouseDownOutsideRef.current = false;
    };

    const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newDate = e.target.value;
        setDate(newDate);
    };

    const handleToggleChange = (checked: boolean) => {
        setIsBlank(checked);
    };

    const handleDownload = () => {
        // Convert date to YYYY-MM-DD format or null if blank
        const dateToPass = isBlank || !date ? null : date;
        // Trigger the download with current values
        onDownload(dateToPass, isBlank, signatureBlockType);
        // Close the modal
        onClose();
    };

    const handleCancel = () => {
        onClose();
    };

    if (!isOpen) return null;

    const modalOverlay: React.CSSProperties = {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10001, // Higher than DownloadModal
    };

    const modalContent: React.CSSProperties = {
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.lg,
        padding: '0',
        width: '500px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        overflow: 'hidden',
        boxShadow: theme.shadows.lg,
        display: 'flex',
        flexDirection: 'column',
    };

    const modalHeader: React.CSSProperties = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: `${theme.spacing.lg} ${theme.spacing.lg}`,
        borderBottom: `1px solid ${theme.colors.border.default}`,
        backgroundColor: theme.colors.primary,
    };

    const modalTitle: React.CSSProperties = {
        margin: 0,
        fontSize: theme.typography.fontSize.xl,
        fontWeight: theme.typography.fontWeight.semibold,
        color: theme.colors.text.inverse,
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing.sm,
    };

    const modalCloseButton: React.CSSProperties = {
        background: 'none',
        border: 'none',
        fontSize: theme.typography.fontSize.xxl,
        color: theme.colors.text.inverse,
        cursor: 'pointer',
        padding: '0',
        width: '32px',
        height: '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: theme.borderRadius.sm,
    };

    const modalBody: React.CSSProperties = {
        padding: theme.spacing.xl,
    };

    const questionText: React.CSSProperties = {
        fontSize: theme.typography.fontSize.md,
        fontWeight: theme.typography.fontWeight.semibold,
        color: theme.colors.primary,
        marginBottom: theme.spacing.md,
        lineHeight: 1.5,
    };

    const inputContainer: React.CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing.md,
        marginBottom: theme.spacing.lg,
    };

    const signatureBlockContainer: React.CSSProperties = {
        marginTop: theme.spacing.md,
        marginBottom: theme.spacing.md,
    };

    const signatureBlockLabel: React.CSSProperties = {
        fontSize: theme.typography.fontSize.md,
        fontWeight: theme.typography.fontWeight.semibold,
        color: theme.colors.primary,
        marginBottom: theme.spacing.sm,
    };

    const signatureBlockOptions: React.CSSProperties = {
        display: 'flex',
        gap: theme.spacing.sm,
    };

    const signatureBlockButton = (isSelected: boolean): React.CSSProperties => ({
        padding: `${theme.spacing.sm} ${theme.spacing.md}`,
        borderRadius: theme.borderRadius.md,
        border: `1px solid ${isSelected ? theme.colors.primary : theme.colors.border.default}`,
        backgroundColor: isSelected ? theme.colors.primary : theme.colors.surface,
        color: isSelected ? theme.colors.text.inverse : theme.colors.text.primary,
        fontSize: theme.typography.fontSize.sm,
        fontWeight: theme.typography.fontWeight.medium,
        cursor: 'pointer',
        transition: 'all 0.2s',
    });

    const dateInput: React.CSSProperties = {
        padding: `${theme.spacing.sm} ${theme.spacing.md}`,
        border: `1px solid ${theme.colors.border.default}`,
        borderRadius: theme.borderRadius.md,
        fontSize: theme.typography.fontSize.md,
        color: theme.colors.text.primary,
        backgroundColor: theme.colors.surface,
        width: '200px',
        cursor: isBlank ? 'not-allowed' : 'text',
        opacity: isBlank ? 0.5 : 1,
    };


    const modalFooter: React.CSSProperties = {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: theme.spacing.md,
        padding: `${theme.spacing.lg} ${theme.spacing.xl}`,
        borderTop: `1px solid ${theme.colors.border.default}`,
    };

    const downloadButton: React.CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing.sm,
        padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
        backgroundColor: theme.colors.primary,
        color: theme.colors.text.inverse,
        border: 'none',
        borderRadius: theme.borderRadius.md,
        fontSize: theme.typography.fontSize.md,
        fontWeight: theme.typography.fontWeight.medium,
        cursor: 'pointer',
        transition: 'opacity 0.2s',
    };

    const cancelButton: React.CSSProperties = {
        padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
        backgroundColor: theme.colors.surface,
        color: theme.colors.text.primary,
        border: `1px solid ${theme.colors.border.default}`,
        borderRadius: theme.borderRadius.md,
        fontSize: theme.typography.fontSize.md,
        fontWeight: theme.typography.fontWeight.medium,
        cursor: 'pointer',
        transition: 'opacity 0.2s',
    };

    return (
        <div
            style={modalOverlay}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
        >
            <div style={modalContent} onMouseDown={(e) => e.stopPropagation()} onMouseUp={(e) => e.stopPropagation()}>
                <div style={modalHeader}>
                    <h2 style={modalTitle}>
                        <span>PRINT FINANCIAL AFFIDAVIT</span>
                    </h2>
                    <button
                        style={modalCloseButton}
                        onClick={handleCancel}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.opacity = '0.7';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.opacity = '1';
                        }}
                    >
                        ×
                    </button>
                </div>
                <div style={modalBody}>
                    <p style={questionText}>
                        Do you want to specify a date the financial affidavit will be signed? If so, enter below. If you elect to leave the date blank, there will be a line for the signer to fill in.
                    </p>
                    <div style={inputContainer}>
                        <input
                            type="date"
                            value={date}
                            onChange={handleDateChange}
                            disabled={isBlank}
                            style={dateInput}
                            placeholder="mm/dd/yyyy"
                        />
                        <CustomToggle
                            checked={isBlank}
                            onChange={handleToggleChange}
                            label="Affidavit Blank"
                            labelStyle={{
                                fontSize: theme.typography.fontSize.md,
                            }}
                        />
                    </div>
                    <div style={signatureBlockContainer}>
                        <div style={signatureBlockLabel}>Which signature block would you like to use?</div>
                        <div style={signatureBlockOptions}>
                            <button
                                type="button"
                                style={signatureBlockButton(signatureBlockType === 'notarySignatureBlock')}
                                onClick={() => setSignatureBlockType('notarySignatureBlock')}
                            >
                                Notary Signature Block
                            </button>
                            <button
                                type="button"
                                style={signatureBlockButton(signatureBlockType === 'penaltyOfPerjury')}
                                onClick={() => setSignatureBlockType('penaltyOfPerjury')}
                            >
                                Penalty of Perjury
                            </button>
                        </div>
                    </div>
                </div>
                <div style={modalFooter}>
                    <button
                        style={downloadButton}
                        onClick={handleDownload}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.opacity = '0.9';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.opacity = '1';
                        }}
                    >
                        <FontAwesomeIcon icon={faDownload} />
                        Download
                    </button>
                    <button
                        style={cancelButton}
                        onClick={handleCancel}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = theme.colors.surfaceSecondary;
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = theme.colors.surface;
                        }}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

